/* ==========================================================================
   OrangeWear — predictive-search.js
   <predictive-search> custom element used by snippets/search-dialog.liquid.
   The input is an ARIA combobox that controls a listbox of suggestions.
   Suggestions are rendered by sections/predictive-search.liquid through the
   Section Rendering API. Focus stays in the input; arrow keys move the
   active option via aria-activedescendant.
   ========================================================================== */
(() => {
  if (customElements.get('predictive-search')) return;

  const SECTION_ID = 'predictive-search';
  const DEBOUNCE_MS = 250;
  const CACHE_LIMIT = 24;

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.controller = null;
      this.timer = null;
      this.activeIndex = -1;
    }

    connectedCallback() {
      this.input = this.querySelector('input[role="combobox"]');
      this.panel = this.querySelector('[data-predictive-search-panel]');
      this.listbox = this.querySelector('[role="listbox"]');
      this.message = this.querySelector('[data-predictive-search-message]');
      this.live = this.querySelector('[data-predictive-search-live]');
      this.dialog = this.closest('dialog');
      if (!this.input || !this.panel || !this.listbox) return;

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      if (this.dialog) {
        this.dialog.addEventListener('dialog:open', this.onDialogOpen);
        this.dialog.addEventListener('dialog:close', this.onDialogClose);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.abort();
      if (this.input) {
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('keydown', this.onKeydown);
      }
      if (this.dialog) {
        this.dialog.removeEventListener('dialog:open', this.onDialogOpen);
        this.dialog.removeEventListener('dialog:close', this.onDialogClose);
      }
    }

    get query() {
      return this.input.value.trim();
    }

    get options() {
      return Array.from(this.listbox.querySelectorAll('[role="option"]'));
    }

    get isOpen() {
      return !this.panel.hidden;
    }

    /* ---------- Events ---------- */
    onInput = () => {
      window.clearTimeout(this.timer);
      if (!this.query) {
        this.abort();
        this.reset();
        return;
      }
      this.timer = window.setTimeout(() => this.search(this.query), DEBOUNCE_MS);
    };

    onKeydown = (event) => {
      if (event.isComposing || event.keyCode === 229) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          const options = this.options;
          if (!options.length) return;
          event.preventDefault();
          if (!this.isOpen) this.open();
          const last = options.length - 1;
          let next;
          if (event.key === 'ArrowDown') {
            next = this.activeIndex >= last ? 0 : this.activeIndex + 1;
          } else {
            next = this.activeIndex <= 0 ? last : this.activeIndex - 1;
          }
          this.setActive(next);
          break;
        }

        case 'Enter': {
          // Without an active option the form submits normally to the search page.
          const option = this.isOpen ? this.options[this.activeIndex] : null;
          const link = option ? option.querySelector('a[href]') : null;
          if (!link) return;
          event.preventDefault();
          link.click();
          break;
        }

        case 'Escape': {
          // First Escape clears the active option; the next one reaches the dialog (native cancel).
          if (this.activeIndex < 0) return;
          event.preventDefault();
          event.stopPropagation();
          this.setActive(-1);
          break;
        }

        default:
          break;
      }
    };

    onDialogOpen = () => {
      this.input.focus({ preventScroll: true });
      if (this.input.value) this.input.select();
    };

    onDialogClose = () => {
      this.setActive(-1);
    };

    /* ---------- Fetching ---------- */
    buildUrl(query) {
      const routes = (window.theme && window.theme.routes) || {};
      const url = new URL(routes.predictiveSearch || '/search/suggest', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', SECTION_ID);
      return url.toString();
    }

    async search(query) {
      if (this.cache.has(query)) {
        this.abort();
        this.render(this.cache.get(query));
        return;
      }

      this.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setLoading(true);

      try {
        const response = await fetch(this.buildUrl(query), {
          signal: controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
        const result = this.parse(await response.text());
        this.remember(query, result);
        if (this.query === query) this.render(result);
      } catch (error) {
        if (error.name !== 'AbortError' && this.query === query) this.reset();
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.setLoading(false);
        }
      }
    }

    abort() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      this.setLoading(false);
    }

    parse(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector('[data-predictive-search-results]');
      const pick = (selector) => (root ? root.querySelector(selector) : null);
      const options = pick('[data-predictive-search-options]');
      const message = pick('[data-predictive-search-message]');
      const status = pick('[data-predictive-search-status]');
      return {
        options: options ? options.innerHTML.trim() : '',
        message: message ? message.textContent.trim() : '',
        status: status ? status.textContent.trim() : '',
      };
    }

    remember(query, result) {
      this.cache.delete(query);
      this.cache.set(query, result);
      if (this.cache.size > CACHE_LIMIT) {
        this.cache.delete(this.cache.keys().next().value);
      }
    }

    /* ---------- Rendering ---------- */
    render({ options, message, status }) {
      if (!options) {
        this.reset();
        return;
      }
      this.listbox.innerHTML = options;
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (this.message) {
        this.message.textContent = message;
        this.message.hidden = !message;
      }
      this.open();
      this.announce(status);
    }

    open() {
      if (!this.listbox.childElementCount) return;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    reset() {
      this.listbox.innerHTML = '';
      if (this.message) {
        this.message.textContent = '';
        this.message.hidden = true;
      }
      this.panel.hidden = true;
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.announce('');
    }

    setActive(index) {
      const options = this.options;
      this.activeIndex = index >= 0 && index < options.length ? index : -1;
      options.forEach((option, i) => {
        option.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
      });
      const active = options[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    setLoading(loading) {
      this.toggleAttribute('loading', loading);
      if (this.listbox) this.listbox.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    announce(text) {
      if (!this.live || this.live.textContent === text) return;
      this.live.textContent = text;
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
